'use client'

import Link from 'next/link'
import React from 'react'

export type ButtonVariant =
  | 'primary'
  | 'accent'
  | 'secondary'
  | 'danger'
  | 'ghost'
  | 'link'
export type ButtonSize = 'md' | 'sm' | 'icon' | 'icon-sm'
export type ButtonTone = 'danger' | 'positive' | 'muted'

export interface DashboardButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /**
   * Recolours the label without changing the variant — a bordered icon button
   * that deletes, for instance. Use this rather than passing a `text-*` class:
   * the variant's own colour is `!important` (see below), so a second important
   * utility would be resolved by Tailwind's emit order rather than by yours.
   */
  tone?: ButtonTone
  /** Renders a next/link styled as a button. Navigation, not submission. */
  href?: string
  /** Leaves the target app; renders a plain anchor. Ignored without `href`. */
  external?: boolean
  loading?: boolean
  loadingLabel?: string
  fullWidth?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  type?: 'button' | 'submit' | 'reset'
}

/**
 * The dashboard button.
 *
 * Before this, the primary button was open-coded at every call site and had
 * drifted into twelve variants: `rounded-lg` in nine places and `rounded-xl` in
 * three, `px-4` against `px-5`, disabled at `opacity-40`, `opacity-50` or not
 * dimmed at all, and `transition-opacity` on some but not others. Sat next to
 * each other in a modal footer or a card header, they visibly failed to match.
 *
 * Three rules are enforced here rather than remembered:
 *
 * - **44px minimum.** `md` and `icon` are `h-11`. `sm` exists at 36px for dense
 *   table rows on a pointer, and should not be used for a phone's primary path.
 * - **Disabled always reads as disabled** — one opacity, plus `cursor-not-allowed`
 *   — and `loading` implies disabled, so a double submit is not possible by
 *   forgetting to pass both.
 * - **`!text-white` on filled variants.** `.dashboard-nav-link` in tokens.css is
 *   unlayered, so its `color` outranks any layered Tailwind utility. It was
 *   added to beat Bootstrap and is still needed to beat the browser's own link
 *   colour, so these bangs stay.
 */

const BASE =
  'dashboard-nav-link inline-flex items-center justify-center gap-2 font-poppins font-semibold transition-opacity transition-colors select-none disabled:cursor-not-allowed disabled:opacity-45 aria-disabled:cursor-not-allowed aria-disabled:opacity-45'

const VARIANTS: Record<ButtonVariant, string> = {
  // Near-black, not the accent. See --dashboard-solid in tokens.css: the accent
  // is for links and active state, which repeat; a filled accent button repeats
  // too and turns the accent into background noise.
  primary:
    'bg-dashboard-solid !text-white shadow-[var(--dashboard-shadow-button)] hover:bg-dashboard-solid-hover',
  // The rare button that should carry the dashboard's colour — a single hero
  // action on an otherwise empty screen. If two are visible at once, one of
  // them wanted `primary`.
  accent: 'bg-dashboard-accent !text-white hover:opacity-90',
  secondary:
    'bg-white !text-dashboard-text border border-dashboard-hairline shadow-[var(--dashboard-shadow-button)] hover:bg-slate-50',
  danger: 'bg-dashboard-negative !text-white hover:opacity-90',
  ghost: 'bg-transparent !text-dashboard-muted hover:bg-dashboard-accent-wash',
  // No box: for "View all" and similar, where a filled control would outrank
  // the thing it sits beside. Matches landlord's link treatment exactly —
  // accent-coloured, semibold, underline on hover.
  link: 'bg-transparent !text-dashboard-accent hover:underline p-0 h-auto',
}

/**
 * Substituted *into* the variant rather than appended after it, so exactly one
 * important text-colour utility is ever on the element.
 */
const TONES: Record<ButtonTone, string> = {
  danger: '!text-dashboard-negative',
  positive: '!text-dashboard-positive',
  muted: '!text-dashboard-muted',
}

/**
 * Pills, and a little more horizontal padding to suit them.
 *
 * These were `rounded-lg` (8px), which made them the least rounded thing on
 * every screen they appeared on — the referral page alone puts them beside six
 * 12px cards and three circular share buttons, so an 8px button read as the odd
 * one out rather than as the primary action. Fully round also removes the
 * question of which radius a button should take at which height: a pill is a
 * pill at 36px and at 44px, where a fixed 12px is proportionally different.
 */
const SIZES: Record<ButtonSize, string> = {
  md: 'h-11 px-5 rounded-full text-[13px]',
  sm: 'h-9 px-4 rounded-full text-[12px]',
  icon: 'h-11 w-11 rounded-full shrink-0',
  'icon-sm': 'h-9 w-9 rounded-full shrink-0',
}

const Spinner = () => (
  <span
    aria-hidden
    className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
  />
)

export const DashboardButton: React.FC<DashboardButtonProps> = ({
  variant = 'primary',
  size = 'md',
  tone,
  href,
  external = false,
  loading = false,
  loadingLabel,
  fullWidth = false,
  leftIcon,
  rightIcon,
  type = 'button',
  className = '',
  children,
  disabled,
  ...rest
}) => {
  const classes = [
    BASE,
    tone ? VARIANTS[variant].replace(/!text-\S+/, TONES[tone]) : VARIANTS[variant],
    variant === 'link' ? '' : SIZES[size],
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const content = (
    <>
      {loading ? <Spinner /> : leftIcon}
      {loading && loadingLabel ? loadingLabel : children}
      {!loading && rightIcon}
    </>
  )

  if (href && !disabled) {
    // A disabled link is not a thing the platform has, so a disabled `href`
    // falls through to a real <button> below and genuinely cannot be activated.
    if (external) {
      return (
        <a href={href} className={classes} {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
          {content}
        </a>
      )
    }

    return (
      <Link href={href} className={classes} {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {content}
      </Link>
    )
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  )
}

/**
 * A square icon button. `label` is required — it becomes both the accessible
 * name and the tooltip, because every one of these was previously an unlabelled
 * glyph that a screen reader announced as "button".
 */
export const DashboardIconButton: React.FC<
  Omit<DashboardButtonProps, 'children' | 'leftIcon' | 'rightIcon' | 'size'> & {
    label: string
    icon: React.ReactNode
    size?: 'icon' | 'icon-sm'
  }
> = ({ label, icon, variant = 'secondary', size = 'icon', ...rest }) => (
  <DashboardButton variant={variant} size={size} aria-label={label} title={label} {...rest}>
    {icon}
  </DashboardButton>
)

export default DashboardButton
